import { useEffect, useMemo, useState } from 'react';
import { X, Search, Lock, RotateCcw, ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import AnchoredPortal from './AnchoredPortal';
import { LEVELS, LEVEL_RANK, levelLabel, levelColor, ghostLevelStyle } from './constants';
import { foldText } from '../../utils/fold';
import { fetchUserBoards, setUserBoardLevel, removeUserBoardLevel } from '../../api/access';

// Painel lateral "Quadros de <pessoa>" (arquitetura-v2.md §6.6): não-modal,
// agrupado por área, com busca, filtro por área e "só exceções". Cada linha
// mostra o nível efetivo e a origem (herdado vs. exceção de quadro), com
// "voltar ao padrão" quando é exceção.
export default function BoardsPanel({ anchorRef, user, role, canEdit, onClose }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [workspaceFilter, setWorkspaceFilter] = useState('all');
  const [onlyOverrides, setOnlyOverrides] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [pending, setPending] = useState(() => new Set());
  const [rowErrors, setRowErrors] = useState({});

  const load = () => {
    setError(null);
    fetchUserBoards(user.id).then(setItems).catch((err) => setError(err.message || 'Falha ao carregar'));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  const workspaceOptions = useMemo(() => {
    if (!items) return [];
    const map = new Map();
    items.forEach((it) => map.set(it.board.workspace_id, it.board.workspace_name));
    return [...map.entries()];
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = foldText(search.trim());
    return items.filter((it) => {
      if (workspaceFilter !== 'all' && String(it.board.workspace_id) !== String(workspaceFilter)) return false;
      if (onlyOverrides && it.source !== 'board') return false;
      if (q && !foldText(it.board.name).includes(q)) return false;
      return true;
    });
  }, [items, search, workspaceFilter, onlyOverrides]);

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((it) => {
      const key = it.board.workspace_id;
      if (!map.has(key)) map.set(key, { name: it.board.workspace_name, items: [] });
      map.get(key).items.push(it);
    });
    return [...map.entries()];
  }, [filtered]);

  const overridesCount = items ? items.filter((it) => it.source === 'board').length : 0;

  const toggleCollapsed = (id) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const setBusy = (id, v) => setPending((prev) => {
    const next = new Set(prev);
    if (v) next.add(id); else next.delete(id);
    return next;
  });

  const runChange = async (boardId, action) => {
    setRowErrors((p) => ({ ...p, [boardId]: null }));
    setBusy(boardId, true);
    try {
      await action();
      load();
    } catch (err) {
      setRowErrors((p) => ({ ...p, [boardId]: err.message || 'Falha ao salvar' }));
    } finally {
      setBusy(boardId, false);
    }
  };

  const handleLevelChange = (item, level) =>
    runChange(item.board.id, () => setUserBoardLevel(user.id, item.board.id, level));

  const handleReset = (item) =>
    runChange(item.board.id, () => removeUserBoardLevel(user.id, item.board.id));

  return (
    <AnchoredPortal anchorRef={anchorRef} onClose={onClose} placement="right-panel" width={460}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 shrink-0">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-text-primary truncate">Quadros de {user.name}</h3>
          <p className="text-[11px] text-text-secondary truncate">
            Grupo: {role?.name || '—'}
          </p>
        </div>
        <button onClick={onClose} className="text-text-secondary hover:text-text-primary shrink-0 ml-2">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-4 py-2.5 border-b border-border/50 space-y-2 shrink-0">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-text-secondary" />
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar quadro…"
            className="w-full bg-main border border-border rounded pl-7 pr-2 py-1.5 text-xs text-text-primary outline-none focus:border-accent/50"
          />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={workspaceFilter}
            onChange={(e) => setWorkspaceFilter(e.target.value)}
            className="flex-1 bg-main border border-border rounded px-2 py-1.5 text-xs text-text-primary outline-none focus:border-accent/50 min-w-0"
          >
            <option value="all">Todas as áreas</option>
            {workspaceOptions.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-[11px] text-text-secondary whitespace-nowrap cursor-pointer select-none shrink-0">
            <input
              type="checkbox"
              checked={onlyOverrides}
              onChange={(e) => setOnlyOverrides(e.target.checked)}
              className="accent-accent"
            />
            Só exceções
          </label>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {error && (
          <div className="px-4 py-4 text-xs text-danger">
            {error} · <button onClick={load} className="underline">Tentar de novo</button>
          </div>
        )}
        {!error && items === null && <div className="px-4 py-4 text-xs text-text-secondary">Carregando…</div>}
        {!error && items !== null && grouped.length === 0 && (
          <div className="px-4 py-4 text-xs text-text-secondary">Nenhum quadro encontrado.</div>
        )}
        {grouped.map(([wsId, group]) => {
          const isCollapsed = collapsed.has(wsId);
          return (
            <div key={wsId} className="border-b border-border/30">
              <button
                onClick={() => toggleCollapsed(wsId)}
                className="w-full flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-text-primary hover:bg-white/[0.02]"
              >
                {isCollapsed ? <ChevronRight className="w-3.5 h-3.5 shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 shrink-0" />}
                <span className="truncate">{group.name}</span>
                <span className="text-text-secondary font-normal ml-auto shrink-0">
                  {group.items.length} {group.items.length === 1 ? 'quadro' : 'quadros'}
                </span>
              </button>
              {!isCollapsed && group.items.map((it) => {
                const isOverride = it.source === 'board';
                const busy = pending.has(it.board.id);
                const rowErr = rowErrors[it.board.id];
                const direction = isOverride && it.default_level
                  ? (LEVEL_RANK[it.level] > LEVEL_RANK[it.default_level] ? 'up'
                    : LEVEL_RANK[it.level] < LEVEL_RANK[it.default_level] ? 'down' : null)
                  : null;
                return (
                  <div
                    key={it.board.id}
                    className={`flex items-center gap-2 pl-9 pr-4 py-1.5 ${busy ? 'opacity-60' : ''} ${rowErr ? 'ring-1 ring-danger ring-inset' : ''}`}
                  >
                    {it.board.visibility === 'private' && (
                      <Lock className="w-3 h-3 text-text-secondary shrink-0" />
                    )}
                    <span className="flex-1 text-xs text-text-primary truncate" title={it.board.name}>
                      {it.board.name}
                    </span>
                    {direction === 'up' && <ChevronUp className="w-3 h-3 text-success shrink-0" />}
                    {direction === 'down' && <ChevronDown className="w-3 h-3 text-danger shrink-0" />}
                    <select
                      value={it.level}
                      disabled={busy || !canEdit}
                      onChange={(e) => handleLevelChange(it, e.target.value)}
                      className="rounded px-1.5 py-1 text-[11px] font-semibold outline-none border shrink-0"
                      style={
                        !isOverride
                          ? ghostLevelStyle(it.level)
                          : { backgroundColor: levelColor(it.level), color: '#fff', borderColor: 'transparent' }
                      }
                    >
                      {LEVELS.map((l) => (
                        <option key={l.value} value={l.value} style={{ color: '#111' }}>{l.label}</option>
                      ))}
                    </select>
                    {isOverride ? (
                      <button
                        type="button"
                        title={`Voltar ao padrão (${levelLabel(it.default_level)})`}
                        disabled={busy || !canEdit}
                        onClick={() => handleReset(it)}
                        className="text-text-secondary hover:text-accent shrink-0"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <span title="Herdado" className="text-white/40 text-[11px] w-4 text-center shrink-0">⌐</span>
                    )}
                    {rowErr && (
                      <button
                        type="button"
                        title={rowErr}
                        onClick={() => setRowErrors((p) => ({ ...p, [it.board.id]: null }))}
                        className="text-[10px] text-danger shrink-0 hover:underline"
                      >
                        erro
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {overridesCount > 0 && (
        <div className="px-4 py-2 border-t border-border/50 text-[11px] text-text-secondary shrink-0">
          {overridesCount} {overridesCount === 1 ? 'exceção' : 'exceções'}
        </div>
      )}
    </AnchoredPortal>
  );
}
